import { Controller } from "@hotwired/stimulus";

const DEFAULT_NOTICE_MESSAGE = "Please update the highlighted fields.";
const DEFAULT_REQUIRED_MESSAGE = "This field is required.";
const DEFAULT_AT_LEAST_ONE_MESSAGE = "Add at least one detail before submitting.";
const RESTORABLE_SERVER_ERROR_RESOLUTIONS = new Set([ "cooked-date-future" ]);

export default class extends Controller {
  static values = {
    noticeMessage: String,
    atLeastOneSelector: String,
    atLeastOneMessage: String,
    atLeastOneErrorId: String
  };

  connect() {
    this.clientErrorByField = new WeakMap();
    this.serverErrorByField = new WeakMap();
    this.resolvableServerErrorByField = new WeakMap();
    this.interactedFields = new WeakSet();
    this.hasAttemptedSubmit = this.hasServerRenderedErrors();
  }

  hasServerRenderedErrors() {
    return Boolean(this.element.querySelector(".ec-field-error:not([data-client-validation='true']):not(.hidden)"));
  }

  validate(event) {
    if (event.submitter?.formNoValidate) return;

    this.hasAttemptedSubmit = true;

    const requiredValid = this.validateRequiredFields();
    const atLeastOneValid = this.validateAtLeastOneRule();
    const proactiveFieldsValid = this.validateProactiveFields();

    this.updateTopNotice();

    if (requiredValid && atLeastOneValid && proactiveFieldsValid) return;

    event.preventDefault();
    this.focusFirstInvalidField();
  }

  handleBlur(event) {
    const target = event?.target;
    if (!this.isProactiveField(target)) return;

    this.interactedFields.add(target);
    this.validateProactiveField(target);
    this.revalidateProactiveDependents(target);
    this.updateTopNotice();
  }

  handleInput(event) {
    const target = event?.target;
    this.revalidateAfterSubmit(target);
    this.validateProactiveFieldIfEligible(target);
    this.revalidateProactiveDependents(target);
    this.updateTopNotice();
  }

  handleChange(event) {
    const target = event?.target;
    if (this.isProactiveField(target)) this.interactedFields.add(target);

    this.revalidateAfterSubmit(target);
    this.validateProactiveFieldIfEligible(target);
    this.revalidateProactiveDependents(target);
    this.updateTopNotice();
  }

  handleStateChange() {
    this.revalidateAfterSubmit();
  }

  revalidateAfterSubmit(target) {
    if (!this.hasAttemptedSubmit && !this.hasResolvableServerErrors()) return;

    if (target && this.element.contains(target) && target.matches("[required]")) {
      this.validateRequiredField(target);
    } else {
      this.validateRequiredFields();
    }

    this.clearResolvableServerErrors();
    this.validateAtLeastOneRule();
    this.updateTopNotice();
  }

  isProactiveField(field) {
    return Boolean(field?.dataset?.proactiveValidation);
  }

  proactiveFields() {
    return Array.from(this.element.querySelectorAll("[data-proactive-validation]")).filter((field) =>
      this.participatesInValidation(field)
    );
  }

  validateProactiveFields() {
    let allValid = true;
    this.proactiveFields().forEach((field) => {
      if (!this.validateProactiveField(field)) allValid = false;
    });
    return allValid;
  }

  validateProactiveFieldIfEligible(field) {
    if (!this.isProactiveField(field)) return true;
    if (!this.hasAttemptedSubmit && !this.interactedFields.has(field) && !this.proactiveErrorIsVisible(field)) {
      return true;
    }

    return this.validateProactiveField(field);
  }

  validateProactiveField(field) {
    if (!this.isProactiveField(field) || !this.participatesInValidation(field)) return true;
    const hasUnresolvedServerError = this.hasUnresolvedServerError(field);
    if (hasUnresolvedServerError) this.rememberServerError(field);
    if ((hasUnresolvedServerError || this.serverErrorByField.has(field)) && this.proactiveFieldIsBrowserValid(field)) {
      this.restoreServerError(field);
      return true;
    }

    if (field.dataset.proactiveValidation === "password-confirmation") {
      return this.validatePasswordConfirmation(field);
    }

    if (!this.fieldHasMeaningfulValue(field)) {
      this.clearProactiveFieldError(field);
      return true;
    }

    if (field.validity?.valid !== false) {
      this.clearProactiveFieldError(field);
      this.markInvalid(field, false);
      return true;
    }

    this.showFieldError(
      field,
      field.dataset.proactiveErrorMessage || field.validationMessage,
      { proactive: true }
    );
    this.markInvalid(field, true);
    return false;
  }

  proactiveFieldIsBrowserValid(field) {
    if (field.matches("[required]") && !this.requiredFieldHasValue(field)) return false;

    if (field.dataset.proactiveValidation === "password-confirmation") {
      const sourceField = this.proactiveSourceField(field);
      if (!sourceField || !this.fieldHasMeaningfulValue(field) || !this.fieldHasMeaningfulValue(sourceField)) {
        return true;
      }

      return sourceField.value === field.value;
    }

    return !this.fieldHasMeaningfulValue(field) || field.validity?.valid !== false;
  }

  rememberServerError(field) {
    if (this.serverErrorByField.has(field)) return;

    const errorElement = this.errorElementForField(field, { create: false });
    if (
      !errorElement ||
      errorElement.classList.contains("hidden") ||
      errorElement.dataset.clientValidation === "true" ||
      errorElement.dataset.clientErrorResolution
    ) return;

    this.serverErrorByField.set(field, { message: errorElement.textContent });
  }

  restoreServerError(field) {
    const serverError = this.serverErrorByField.get(field);
    if (!serverError) return;

    const errorElement = this.errorElementForField(field, { create: false });
    if (!errorElement) return;

    errorElement.textContent = serverError.message;
    errorElement.classList.remove("hidden");
    delete errorElement.dataset.clientValidation;
    delete errorElement.dataset.proactiveValidation;
    this.markInvalid(field, true);
  }

  validatePasswordConfirmation(field) {
    const sourceField = this.proactiveSourceField(field);
    if (!sourceField || !this.fieldHasMeaningfulValue(field) || !this.fieldHasMeaningfulValue(sourceField)) {
      this.clearProactiveFieldError(field);
      return true;
    }

    if (sourceField.value === field.value) {
      this.clearProactiveFieldError(field);
      this.markInvalid(field, false);
      return true;
    }

    this.showFieldError(
      field,
      field.dataset.proactiveErrorMessage || "The values do not match.",
      { proactive: true }
    );
    this.markInvalid(field, true);
    return false;
  }

  proactiveSourceField(field) {
    const sourceId = field?.dataset?.proactiveValidationSource;
    return sourceId ? this.element.querySelector(`#${sourceId}`) : null;
  }

  revalidateProactiveDependents(sourceField) {
    if (!sourceField?.id) return;

    this.proactiveFields()
      .filter((field) => field.dataset.proactiveValidationSource === sourceField.id)
      .forEach((field) => {
        if (this.hasAttemptedSubmit || this.interactedFields.has(field) || this.proactiveErrorIsVisible(field)) {
          this.validateProactiveField(field);
        }
      });
  }

  proactiveErrorIsVisible(field) {
    const errorElement = this.errorElementForField(field, { create: false });
    return Boolean(
      errorElement?.dataset.proactiveValidation === "true" &&
      !errorElement.classList.contains("hidden")
    );
  }

  fieldHasMeaningfulValue(field) {
    if (!field) return false;

    return field.type === "password" ? field.value !== "" : field.value.trim() !== "";
  }

  clearProactiveFieldError(field) {
    const errorElement = this.errorElementForField(field, { create: false });
    if (errorElement?.dataset.proactiveValidation !== "true") return;

    this.clearFieldError(field);
    this.markInvalid(field, false);
    delete errorElement.dataset.proactiveValidation;
  }

  hasResolvableServerErrors() {
    return this.resolvableServerErrorFields().length > 0;
  }

  resolvableServerErrorFields() {
    return Array.from(this.element.querySelectorAll("input, select, textarea")).filter((field) => {
      const errorElement = this.errorElementForField(field, { create: false });
      return errorElement?.dataset.clientErrorResolution && (
        errorElement.dataset.clientValidation !== "true" || this.resolvableServerErrorByField.has(field)
      );
    });
  }

  clearResolvableServerErrors() {
    this.resolvableServerErrorFields().forEach((field) => {
      const errorElement = this.errorElementForField(field, { create: false });
      if (this.serverErrorIsResolved(field, errorElement)) {
        this.rememberResolvableServerError(field, errorElement);
      } else {
        this.restoreResolvableServerError(field, errorElement);
        return;
      }

      errorElement.textContent = "";
      errorElement.classList.add("hidden");
      errorElement.dataset.clientValidation = "true";
      this.markInvalid(field, false);
    });
  }

  serverErrorIsResolved(field, errorElement) {
    switch (errorElement?.dataset.clientErrorResolution) {
      case "required":
        return this.requiredFieldHasValue(field);
      case "email-format":
        return field.value.trim() !== "" && field.validity?.typeMismatch === false;
      case "password-length":
        return field.value !== "" && field.validity?.tooShort === false;
      case "password-confirmation": {
        const sourceField = this.element.querySelector(`#${errorElement.dataset.clientErrorResolutionField}`);
        return field.value !== "" && sourceField?.value === field.value;
      }
      case "cooked-date-future":
        return field.value !== "" && (!field.max || field.value <= field.max);
      default:
        return false;
    }
  }

  rememberResolvableServerError(field, errorElement) {
    const resolution = errorElement?.dataset.clientErrorResolution;
    if (!RESTORABLE_SERVER_ERROR_RESOLUTIONS.has(resolution) || this.resolvableServerErrorByField.has(field)) return;
    if (!errorElement || errorElement.classList.contains("hidden") || errorElement.dataset.clientValidation === "true") return;

    this.resolvableServerErrorByField.set(field, { message: errorElement.textContent });
  }

  restoreResolvableServerError(field, errorElement) {
    const serverError = this.resolvableServerErrorByField.get(field);
    if (
      !serverError ||
      !errorElement ||
      (errorElement.dataset.clientValidation === "true" && !errorElement.classList.contains("hidden"))
    ) return;

    errorElement.textContent = serverError.message;
    errorElement.classList.remove("hidden");
    delete errorElement.dataset.clientValidation;
    delete errorElement.dataset.proactiveValidation;
    this.markInvalid(field, true);
  }

  validateRequiredFields() {
    const radioGroupNames = new Set();
    let allValid = true;

    this.requiredFields().forEach((field) => {
      if (field.type === "radio") {
        const groupKey = field.name || field.id;
        if (radioGroupNames.has(groupKey)) return;
        radioGroupNames.add(groupKey);
      }

      if (!this.validateRequiredField(field)) allValid = false;
    });

    return allValid;
  }

  validateRequiredField(field) {
    if (!this.participatesInValidation(field)) return true;

    const valid = this.requiredFieldHasValue(field);
    const fieldsToMark = field.type === "radio" ? this.radioGroupFields(field) : [ field ];
    const hasUnresolvedServerError = this.hasUnresolvedServerError(field);

    fieldsToMark.forEach((candidate) => this.markInvalid(candidate, !valid || hasUnresolvedServerError));

    if (valid) {
      this.clearFieldError(field);
      return true;
    }

    const message = field.dataset.requiredMessage || this.requiredMessageFor(field);
    this.showFieldError(field, message);
    return false;
  }

  hasUnresolvedServerError(field) {
    const errorElement = this.errorElementForField(field, { create: false });
    return Boolean(errorElement && !errorElement.classList.contains("hidden") && errorElement.dataset.clientValidation !== "true");
  }

  requiredFields() {
    return Array.from(this.element.querySelectorAll("[required]")).filter((field) =>
      this.participatesInValidation(field)
    );
  }

  participatesInValidation(field) {
    if (!field) return false;
    if (field.disabled) return false;
    if (field.type === "hidden") return false;

    return true;
  }

  requiredFieldHasValue(field) {
    if (!field) return true;

    if (field.type === "radio") {
      return this.radioGroupFields(field).some((candidate) => candidate.checked);
    }

    if (field.type === "checkbox") {
      return field.checked;
    }

    if (field.type === "file") {
      return (field.files?.length || 0) > 0;
    }

    if (field.tagName === "SELECT" && field.multiple) {
      return Array.from(field.selectedOptions).some((option) => option.value !== "");
    }

    return field.value.trim() !== "";
  }

  radioGroupFields(field) {
    if (!field?.name) return [ field ].filter(Boolean);

    const escaped = window.CSS?.escape ? window.CSS.escape(field.name) : field.name.replace(/"/g, '\\"');
    return Array.from(this.element.querySelectorAll(`input[type="radio"][name="${escaped}"]`));
  }

  requiredMessageFor(field) {
    const label = this.labelTextForField(field);

    if (field.type === "checkbox") {
      return label ? `Please check "${label}"` : DEFAULT_REQUIRED_MESSAGE;
    }

    if (field.type === "radio") {
      return label ? `Please select ${label.toLowerCase()}.` : DEFAULT_REQUIRED_MESSAGE;
    }

    return label ? `${label} is required.` : DEFAULT_REQUIRED_MESSAGE;
  }

  labelTextForField(field) {
    if (!field) return "";

    let label = null;

    if (field.id) {
      label = this.element.querySelector(`label[for="${field.id}"]`);
    }

    if (!label) {
      label = field.closest(".ec-field-group, .ec-form-block, fieldset")?.querySelector("label, legend");
    }

    if (!label) return "";

    return label.textContent.replace(/\s+/g, " ").trim().replace(/[:*]\s*$/, "");
  }

  showFieldError(field, message, { proactive = false } = {}) {
    const errorElement = this.errorElementForField(field);
    if (!errorElement) return;

    this.rememberServerError(field);
    errorElement.textContent = message;
    errorElement.classList.remove("hidden");
    errorElement.dataset.clientValidation = "true";
    if (proactive) {
      errorElement.dataset.proactiveValidation = "true";
    } else {
      delete errorElement.dataset.proactiveValidation;
    }
  }

  clearFieldError(field) {
    const errorElement = this.errorElementForField(field, { create: false });
    if (!errorElement) return;
    if (errorElement.dataset.clientValidation !== "true") return;

    errorElement.textContent = "";
    errorElement.classList.add("hidden");
    delete errorElement.dataset.proactiveValidation;
  }

  errorElementForField(field, options = {}) {
    if (!field) return null;

    const create = options.create !== false;
    const messageField = field.type === "radio" ? this.radioGroupFields(field)[0] : field;

    if (!messageField) return null;

    const cached = this.clientErrorByField.get(messageField);
    if (cached) return cached;

    const fromAria = this.errorElementFromAriaDescribedBy(messageField);
    if (fromAria) {
      this.clientErrorByField.set(messageField, fromAria);
      return fromAria;
    }

    if (!create) return null;

    const created = this.createFieldErrorElement(messageField);
    if (created) this.clientErrorByField.set(messageField, created);
    return created;
  }

  errorElementFromAriaDescribedBy(field) {
    const ids = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    return ids
      .map((id) => this.element.querySelector(`#${id}`))
      .find((candidate) => candidate && candidate.classList.contains("ec-field-error")) || null;
  }

  createFieldErrorElement(field) {
    const container = field.closest(".ec-field-group, .ec-form-block, fieldset") || field.parentElement;
    if (!container) return null;

    const element = document.createElement("p");
    element.className = "ec-field-error hidden";
    element.dataset.clientValidation = "true";

    const describedByIds = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    const reusableErrorId = describedByIds.find((id) =>
      id.endsWith("-error") && !this.element.querySelector(`#${id}`)
    );

    if (reusableErrorId) {
      element.id = reusableErrorId;
    } else if (field.id) {
      element.id = `${field.id}-client-error`;
    }

    if (element.id) this.appendDescribedBy(field, element.id);

    container.appendChild(element);
    return element;
  }

  appendDescribedBy(field, id) {
    if (!id) return;

    const existing = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    if (existing.includes(id)) return;

    existing.push(id);
    field.setAttribute("aria-describedby", existing.join(" "));
  }

  validateAtLeastOneRule() {
    if (!this.hasAtLeastOneSelectorValue) return true;

    const fields = this.atLeastOneFields();
    if (fields.length === 0) return true;

    const valid = fields.some((field) => this.fieldHasAnyValue(field));
    const errorElement = this.atLeastOneErrorElement(fields[0]);

    if (valid) {
      fields.forEach((field) => this.markInvalid(field, false));
      this.clearAtLeastOneError(errorElement);
      return true;
    }

    fields.forEach((field) => this.markInvalid(field, true));
    this.showAtLeastOneError(errorElement);
    return false;
  }

  atLeastOneFields() {
    return Array.from(this.element.querySelectorAll(this.atLeastOneSelectorValue)).filter((field) =>
      this.participatesInAtLeastOneValidation(field)
    );
  }

  participatesInAtLeastOneValidation(field) {
    return Boolean(field) && !field.disabled;
  }

  fieldHasAnyValue(field) {
    if (!field) return false;

    if (field.type === "checkbox" || field.type === "radio") {
      return field.checked;
    }

    if (field.type === "file") {
      return (field.files?.length || 0) > 0;
    }

    return field.value.trim() !== "";
  }

  atLeastOneErrorElement(fallbackField) {
    if (this.hasAtLeastOneErrorIdValue) {
      const fromId = this.element.querySelector(`#${this.atLeastOneErrorIdValue}`);
      if (fromId) return fromId;
    }

    return this.errorElementForField(fallbackField);
  }

  showAtLeastOneError(errorElement) {
    if (!errorElement) return;

    errorElement.textContent = this.hasAtLeastOneMessageValue ?
      this.atLeastOneMessageValue :
      DEFAULT_AT_LEAST_ONE_MESSAGE;
    errorElement.classList.remove("hidden");
    errorElement.dataset.clientValidation = "true";
  }

  clearAtLeastOneError(errorElement) {
    if (!errorElement) return;
    if (errorElement.dataset.clientValidation !== "true") return;

    errorElement.textContent = "";
    errorElement.classList.add("hidden");
  }

  markInvalid(field, invalid) {
    if (!field) return;

    field.classList.toggle("ec-field-control--invalid", invalid);
    field.setAttribute("aria-invalid", invalid ? "true" : "false");
  }

  updateTopNotice() {
    const notice = this.topNoticeElement();
    if (!notice) return;

    const hasClientErrors = this.element.querySelector(".ec-field-error[data-client-validation='true']:not(.hidden)");
    const hasServerErrors = this.element.querySelector(".ec-field-error:not([data-client-validation='true']):not(.hidden)");
    const shouldShowNotice = hasServerErrors || hasClientErrors;
    if (!shouldShowNotice) {
      notice.textContent = "";
      notice.classList.add("hidden");
      return;
    }

    notice.textContent = this.hasNoticeMessageValue ? this.noticeMessageValue : DEFAULT_NOTICE_MESSAGE;
    notice.classList.remove("hidden");
  }

  topNoticeElement() {
    if (this.noticeElement) return this.noticeElement;

    const existing = this.element.querySelector("[data-client-validation-notice]");
    if (existing) {
      this.noticeElement = existing;
      return existing;
    }

    const notice = document.createElement("p");
    notice.className = "ec-form-error-notice hidden";
    notice.dataset.clientValidationNotice = "true";
    notice.setAttribute("role", "alert");
    notice.setAttribute("aria-live", "polite");
    notice.setAttribute("aria-atomic", "true");

    this.element.prepend(notice);
    this.noticeElement = notice;
    return notice;
  }

  focusFirstInvalidField() {
    const firstInvalid = this.element.querySelector("[aria-invalid='true']:not([disabled])");
    firstInvalid?.focus();
  }
}
